import styles from './additionalFacetDrawer.module.scss'
import FacetSection from './facetSection/facetSection'

import React, { useContext, useEffect, useState } from 'react'

import { useVehicleStore } from 'Stores/VehicleStore'

import {
  addToGarage,
  showAddToGarage,
  showUpdateCustomerProject,
  updateCustomerProject,
} from 'Utilities/CustomerProjectUtilities/customerProjectUtilities'
import getFitmentDetailsFromSelectedFacets from 'Utilities/CustomerProjectUtilities/getFitmentDetailsFromSelectedFacets'
import getMatchingProject from 'Utilities/CustomerProjectUtilities/getMatchingProject'
import { createCheckFitmentRequest } from 'Utilities/fitment/checkFitmentUtils'

import { NavigationContext } from 'Contexts/Navigation/NavigationContext'
import { NotificationContext } from 'Contexts/NotificationContext/NotificationContext'
import { useSkuBaseFitmentData } from 'Contexts/UserSpecificProductDataStore/UserSpecificProductDataStore'

import useSegment from 'Hooks/useSegment'

import Button from 'Molecules/Button'
import Drawer from 'Molecules/NewDrawer/Drawer'

import CoveringSpinner from 'Atoms/CoveringSpinner/CoveringSpinner'
import Panel from 'Atoms/Panel'
import Typography from 'Atoms/Typography'

const engineLevelFacetOptions = {
  engineDisplacement: 'GA_EngineDisplacement',
  engineVersion: 'GA_EngineVersion',
  engineQualifier: 'GA_EngineQualifier',
  engineCode: 'GA_EngineCode',
  engineCamshaftType: 'GA_EngineCamshaftType',
  engineFuelSystem: 'GA_EngineFuelSystem',
}

const facetTypeDisplayNames = [
  { key: 'GA_EngineDisplacement', value: 'Engine Displacement' },
  { key: 'GA_EngineVersion', value: 'Engine Version' },
  { key: 'GA_EngineQualifier', value: 'Engine Qualifier' },
  { key: 'GA_EngineCode', value: 'Engine Code' },
  { key: 'GA_EngineCamshaftType', value: 'Engine Camshaft Type' },
  { key: 'GA_EngineFuelSystem', value: 'Engine Fuel System' },
]

const isFacetOptionHeirarchial = (facetType) => {
  switch (facetType) {
    case engineLevelFacetOptions.engineDisplacement:
    case engineLevelFacetOptions.engineVersion:
    case engineLevelFacetOptions.engineQualifier:
      return true
    default:
      return false
  }
}

const AdditionalFacetDrawer = ({
  fitment,
  checkFitment,
  skuBaseNumber,
  skuVariantNumber,
  searchResponseFacets,
  selectedFacetOptions,
  vehicleName,
}) => {
  const [selectedFacetIds, setSelectedFacetIds] = useState({})
  const [facetLevels, setFacetLevels] = useState([])
  const [isVerifyFitmentButtonDisabled, setIsVerifyFitmentButtonDisabled] = useState(true)
  const [isVerifyingFitment, setIsVerifyingFitment] = useState(false)

  const customerProjects = useVehicleStore((x) => x.context.customerProjects)
  const selectedCustomerProjectState = useVehicleStore((x) => x.context.selectedCustomerProject)
  const selectedFacets = selectedFacetOptions || []
  vehicleName = vehicleName || ''

  const updateCustomerProjectData = useVehicleStore((x) => x.updateCustomerProjectData)
  const { sendCustomSegmentTrackEvent } = useSegment()
  const selectCustomerProject = useVehicleStore((x) => x.selectCustomerProject)
  const addCustomerProject = useVehicleStore((x) => x.addCustomerProject)
  const { PushNotification } = useContext(NotificationContext)

  const { state: navigationState, dispatch: navigationDispatch } = useContext(NavigationContext)
  const { garage } = navigationState
  const { isAdditionalFacetDrawerOpen, skuBaseOverride } = garage
  const fitmentDetails = getFitmentDetailsFromSelectedFacets(selectedFacets) || {}

  const canAdd = showAddToGarage(customerProjects, fitmentDetails)
  const canUpdate = showUpdateCustomerProject(customerProjects, fitmentDetails)

  skuBaseNumber = skuBaseOverride ?? skuBaseNumber

  let selectedFitment = useSkuBaseFitmentData(skuBaseOverride)
  if (!selectedFitment) {
    selectedFitment =
      fitment?.length > 0 && fitment.find((fitment) => fitment.key === selectedCustomerProjectState.projectId)?.value
  }

  const hideAdditionalFacetDrawer = () => {
    if (isAdditionalFacetDrawerOpen) {
      navigationDispatch({
        type: 'setAdditionalFacetDrawerState',
        payload: {
          garageState: {
            isAdditionalFacetDrawerOpen: false,
            skuBaseOverride: null,
          },
        },
      })
    }
  }

  const handleSelectedFacetIdChange = (facetType, facetId, facets) => {
    let newSelectedFacetIds
    if (isFacetOptionHeirarchial(facetType)) {
      const existingDisplacement = selectedFacetIds[engineLevelFacetOptions.engineDisplacement]
      const existingVersion = selectedFacetIds[engineLevelFacetOptions.engineVersion]

      const hasVersionNotDisplacement = facets.find((x) => x.id === parseInt(facetId))?.hasVersionNotDisplacement

      if (facetType === engineLevelFacetOptions.engineDisplacement && !hasVersionNotDisplacement) {
        newSelectedFacetIds = { [facetType]: parseInt(facetId) }
      } else if (facetType === engineLevelFacetOptions.engineVersion) {
        newSelectedFacetIds = { [facetType]: parseInt(facetId) }
        if (existingDisplacement) newSelectedFacetIds[engineLevelFacetOptions.engineDisplacement] = existingDisplacement
      } else if (facetType === engineLevelFacetOptions.engineQualifier) {
        newSelectedFacetIds = { [facetType]: parseInt(facetId) }
        if (existingDisplacement) newSelectedFacetIds[engineLevelFacetOptions.engineDisplacement] = existingDisplacement
        if (existingVersion) newSelectedFacetIds[engineLevelFacetOptions.engineVersion] = existingVersion
      } else if (facetType === engineLevelFacetOptions.engineDisplacement && hasVersionNotDisplacement) {
        // SBC 602/604 edge case DO NOT PERSIST EXISTING DISPLACEMENT AND VERSION
        newSelectedFacetIds = {
          [engineLevelFacetOptions.engineVersion]: parseInt(facetId),
        }
      }
    } else {
      newSelectedFacetIds = {
        ...selectedFacetIds,
        [facetType]: parseInt(facetId),
      }
    }

    setSelectedFacetIds(newSelectedFacetIds)
  }

  const removeFacetLevels = () => {
    const numSelectedFacetIds = Object.keys(selectedFacetIds).length

    const newFacetLevels = []
    for (let i = 0; i < numSelectedFacetIds; i++) newFacetLevels.push(facetLevels[i])

    setFacetLevels(newFacetLevels)
  }

  const buildFacetLevels = (additionalEngineLevelFacets) => {
    const facetType = additionalEngineLevelFacets.type
    let newFacetLevels = []
    const existingDisplacement = facetLevels.find(
      (facetLevel) => facetLevel.type === engineLevelFacetOptions.engineDisplacement,
    )
    const existingVersion = facetLevels.find((facetLevel) => facetLevel.type === engineLevelFacetOptions.engineVersion)

    if (facetType === engineLevelFacetOptions.engineDisplacement) newFacetLevels.push(additionalEngineLevelFacets)
    else if (facetType === engineLevelFacetOptions.engineVersion) {
      if (existingDisplacement) newFacetLevels.push(existingDisplacement)
      newFacetLevels.push(additionalEngineLevelFacets)
    } else if (facetType === engineLevelFacetOptions.engineQualifier) {
      if (existingDisplacement) newFacetLevels.push(existingDisplacement)
      if (existingVersion) newFacetLevels.push(existingVersion)
      newFacetLevels.push(additionalEngineLevelFacets)
    } else {
      newFacetLevels = [...facetLevels, additionalEngineLevelFacets]
    }

    return newFacetLevels
  }

  useEffect(() => {
    const selectedFacetIdsLength = Object.keys(selectedFacetIds).length
    if (selectedFacetIdsLength > 0) {
      const facetType = facetLevels?.[0]?.type
      const event = {
        event: 'Additional Facet Selected',
        properties: {
          additionalEngineFacetType: facetTypeDisplayNames.find((x) => x.key == facetType.toString())?.value,
          skuNumber: skuVariantNumber ?? skuBaseNumber,
        },
      }
      sendCustomSegmentTrackEvent(event)
      verifyFitment()
    }
  }, [JSON.stringify(selectedFacetIds)])

  useEffect(() => {
    if (selectedFitment) {
      const needsMoreInfo = selectedFitment.needsMoreInfo
      const selectedFacetIdsLength = Object.keys(selectedFacetIds).length

      if (needsMoreInfo === false) {
        if (selectedFacetIdsLength === facetLevels.length) {
          setIsVerifyFitmentButtonDisabled(false)
          hideAdditionalFacetDrawer()
        }
      } else setIsVerifyFitmentButtonDisabled(true)
    }
  }, [selectedFitment, JSON.stringify(facetLevels), JSON.stringify(selectedFacetIds)])

  useEffect(() => {
    if (selectedFitment) {
      try {
        const needsMoreInfo = selectedFitment.needsMoreInfo
        if (needsMoreInfo === true) {
          const additionalEngineLevelFacets = selectedFitment.additionalEngineLevelFacets
          if (additionalEngineLevelFacets) {
            if (searchResponseFacets) {
              let facet
              if (additionalEngineLevelFacets.displayName === 'Engine Displacement') {
                facet = searchResponseFacets.find((x) => x.facetName === 'Displacement' && x.facetType === 'UNKNOWN')
              } else if (additionalEngineLevelFacets.displayName === 'Engine Qualifier') {
                facet = searchResponseFacets.find((x) => x.facetName === 'Engine Version')
              } else {
                facet = searchResponseFacets.find((x) => x.facetName === additionalEngineLevelFacets.displayName)
              }
              if (facet) {
                const mappedFacets = mapSearchResponseFacetToFacetLevels(facet)
                setFacetLevels(mappedFacets)
              }
            } else {
              const newFacetLevels = buildFacetLevels(additionalEngineLevelFacets)
              setFacetLevels(newFacetLevels)
            }
          }
        } else {
          removeFacetLevels()
        }
      } catch (e) {
        console.error(e)
      }
    }
    setIsVerifyingFitment(false)
  }, [selectedFitment])

  const hasFacetLevelOfType = (facetType) => {
    return facetLevels.find((facetLevel) => facetLevel.type === facetType)
  }

  const verifyFitment = async () => {
    setIsVerifyingFitment(true)
    let selectedCustomerProjectId = selectedCustomerProjectState.projectId
    let selectedCustomerProject = customerProjects?.find(
      (customerVehicle) => customerVehicle.projectId === selectedCustomerProjectId,
    )

    if (canUpdate) {
      selectedCustomerProject = await updateCustomerProject(
        fitmentDetails,
        canUpdate,
        () => {},
        selectCustomerProject,
        updateCustomerProjectData,
        PushNotification,
        vehicleName,
      )
    } else if (canAdd) {
      selectedCustomerProject = await addToGarage(
        fitmentDetails,
        () => {},
        selectCustomerProject,
        addCustomerProject,
        PushNotification,
        vehicleName,
      )
    } else if (!selectedCustomerProject) {
      selectedCustomerProject = getMatchingProject(fitmentDetails, customerProjects)
      if (selectedCustomerProject) {
        selectedCustomerProjectId = selectedCustomerProject?.projectId
        selectCustomerProject(selectedCustomerProject?.projectId)
      }
    }

    if (selectedFacetIds[engineLevelFacetOptions.engineDisplacement]) {
      selectedCustomerProject.engineDisplacementId = selectedFacetIds[engineLevelFacetOptions.engineDisplacement]
    } else if (hasFacetLevelOfType(engineLevelFacetOptions.engineDisplacement))
      selectedCustomerProject.engineDisplacementId = null

    if (selectedFacetIds[engineLevelFacetOptions.engineVersion]) {
      selectedCustomerProject.engineVersionId = selectedFacetIds[engineLevelFacetOptions.engineVersion]
    } else if (hasFacetLevelOfType(engineLevelFacetOptions.engineVersion))
      selectedCustomerProject.engineVersionId = null

    if (selectedFacetIds[engineLevelFacetOptions.engineQualifier]) {
      selectedCustomerProject.engineQualifierId = selectedFacetIds[engineLevelFacetOptions.engineQualifier]
    } else if (hasFacetLevelOfType(engineLevelFacetOptions.engineQualifier)) {
      selectedCustomerProject.engineQualifierId = null
    }

    if (selectedFacetIds[engineLevelFacetOptions.engineCamshaftType]) {
      selectedCustomerProject.engineCamshaftTypeId = selectedFacetIds[engineLevelFacetOptions.engineCamshaftType]
    } else if (hasFacetLevelOfType(engineLevelFacetOptions.engineCamshaftType)) {
      selectedCustomerProject.engineCamshaftTypeId = null
    }

    if (selectedFacetIds[engineLevelFacetOptions.engineCode]) {
      selectedCustomerProject.engineCodeId = selectedFacetIds[engineLevelFacetOptions.engineCode]
    } else if (hasFacetLevelOfType(engineLevelFacetOptions.engineCode)) {
      selectedCustomerProject.engineCodeId = null
    }

    if (selectedFacetIds[engineLevelFacetOptions.engineFuelSystem]) {
      selectedCustomerProject.engineFuelSystemId = selectedFacetIds[engineLevelFacetOptions.engineFuelSystem]
    } else if (hasFacetLevelOfType(engineLevelFacetOptions.engineFuelSystem)) {
      selectedCustomerProject.engineFuelSystemId = null
    }

    let selectedFacet

    if (checkFitment) {
      const checkFitmentRequest = createCheckFitmentRequest(customerProjects)

      checkFitment(checkFitmentRequest)
    } else {
      selectedFacet = facetLevels
        .find((x) => x.type === 'SearchResponseFacet')
        ?.facets?.find((x) => x.id === selectedFacetIds.SearchResponseFacet)
      if (selectedFacet && selectedCustomerProject) {
        if (!selectedCustomerProject.engineVersionId)
          selectedCustomerProject.engineVersionId = selectedFacet.engineFitmentDetails.engineVersionId
        if (!selectedCustomerProject.engineQualifierId)
          selectedCustomerProject.engineQualifierId = selectedFacet.engineFitmentDetails.engineQualifierId
        if (!selectedCustomerProject.engineDisplacementId)
          selectedCustomerProject.engineDisplacementId = selectedFacet.engineFitmentDetails.engineDisplacementId
        if (!selectedCustomerProject.engineCodeId)
          selectedCustomerProject.engineCodeId = selectedFacet.engineFitmentDetails.engineCodeId
        if (!selectedCustomerProject.engineCamshaftTypeId)
          selectedCustomerProject.engineCamshaftTypeId = selectedFacet.engineFitmentDetails.engineCamshaftTypeId
        if (!selectedCustomerProject.engineFuelSystemId)
          selectedCustomerProject.engineFuelSystemId = selectedFacet.engineFitmentDetails.engineFuelSystemId
      }
    }

    await updateCustomerProjectData({
      year: selectedCustomerProject.year,
      makeId: selectedCustomerProject.makeId,
      modelId: selectedCustomerProject.modelId,
      raceTypeId: selectedCustomerProject.raceTypeId,
      vehicleBaseId: selectedCustomerProject.vehicleBaseId,
      engineIds: {
        engineManufacturerId: selectedCustomerProject.engineManufacturerId,
        engineMakeId: selectedCustomerProject.engineMakeId,
        engineSeriesId: selectedCustomerProject.engineSeriesId,
        engineConfigurationId: selectedCustomerProject.engineConfigurationId,
        engineVersionId: selectedCustomerProject.engineVersionId,
        engineQualifierId: selectedCustomerProject.engineQualifierId,
        engineDisplacementId: selectedCustomerProject.engineDisplacementId,
        engineCodeId: selectedCustomerProject.engineCodeId,
        engineFuelSystemId: selectedCustomerProject.engineFuelSystemId,
        engineCamshaftTypeId: selectedCustomerProject.engineCamshaftTypeId,
      },
      customerProjectId: selectedCustomerProjectId,
    })

    if (selectedFacet?.url) {
      window.location.href = selectedFacet.url
    }

    hideAdditionalFacetDrawer()
  }

  const handleVerifyFitmentClick = () => {
    hideAdditionalFacetDrawer()
  }

  if (!selectedFitment) return

  return (
    <Drawer
      location={'right'}
      isOpen={!!isAdditionalFacetDrawerOpen}
      onClose={() => hideAdditionalFacetDrawer()}
      title={'Additional information needed to verify fitment'}
    >
      <Panel className={styles.drawer} layer={1}>
        <CoveringSpinner isLoading={isVerifyingFitment}>
          <div className={styles.coveringSpinnerContent}>
            <Typography className={styles.correctOptionText} font={'bold'} size={1}>
              Select your engine details
            </Typography>
            <Panel className={styles.drawer_panel} layer={0}>
              {facetLevels.map((facetLevel, facetLevelIndex) => {
                const facetType = facetLevel.type
                return (
                  <FacetSection
                    key={'additional_facet_level_' + facetLevelIndex}
                    facetInfo={facetLevel}
                    selectedFacetId={selectedFacetIds[facetType]}
                    setSelectedFacetId={(id) => handleSelectedFacetIdChange(facetType, id, facetLevel.facets)}
                  />
                )
              })}
              <div className={styles.buttonContainer}>
                <Button
                  intent={'positive'}
                  disabled={isVerifyFitmentButtonDisabled}
                  text={'Verify Fitment'}
                  onClick={handleVerifyFitmentClick}
                  size={'small'}
                  segmentEvent={{
                    event: 'Test',
                    properties: {
                      element: 'TestElement',
                    },
                  }}
                />
              </div>
            </Panel>
          </div>
        </CoveringSpinner>
      </Panel>
    </Drawer>
  )
}

const mapSearchResponseFacetToFacetLevels = (facet) => {
  return [
    {
      displayName: facet.facetName,
      facets: (facet.searchResponseFacetOptions || []).map((x, i) => ({
        id: i,
        name: x.displayValue,
        url: x.facetOptionLink.url,
        engineFitmentDetails: {
          engineVersionId: x.engineFitmentFacetOptionDetails?.engineVersionId,
          engineQualifierId: x.engineFitmentFacetOptionDetails?.engineQualifierId,
          engineDisplacementId: x.engineFitmentFacetOptionDetails?.engineDisplacementId,
          engineCodeId:
            (x.engineFitmentFacetOptionDetails?.engineCodeId ?? facet.facetType === 'GA_EngineCode') ? x.id : null,
          engineCamshaftTypeId:
            (x.engineFitmentFacetOptionDetails?.engineCamshaftTypeId ?? facet.facetType === 'GA_EngineCamshaftType')
              ? x.id
              : null,
          engineFuelSystemId:
            (x.engineFitmentFacetOptionDetails?.engineFuelSystemId ?? facet.facetType === 'GA_EngineFuelSystem')
              ? x.id
              : null,
        },
      })),
      type: 'SearchResponseFacet',
    },
  ]
}

export default AdditionalFacetDrawer
